import { normalizeCatalogProperties } from './catalogPropertyFields';
import { CatalogProductProperties } from '../../../api/types';
import { AdminBootstrapDictionaries, AdminCatalogCategory, AdminCatalogProduct, AdminCatalogCategoryCreateRequest, AdminCatalogCategoryUpdateRequest, AdminCatalogProductCreateRequest, AdminCatalogProductUpdateRequest } from '../../../api/types';

export interface CatalogCategoryFormState {
  category_parent_id: string;
  category_name: string;
  category_description: string;
  category_order: string;
}

export interface CatalogProductFormState {
  product_brand_id: string;
  product_manufacturer_id: string;
  product_category_ids: string[];
  product_properties: CatalogProductProperties;
  product_requires_unit_review: boolean;

  product_category_id: string;
  product_sku: string;
  product_name: string;
  product_unit_id: string;
  product_description: string;
  product_status_id: string;
}

export function defaultCatalogProductForm(dictionaries: AdminBootstrapDictionaries): CatalogProductFormState {
  const firstUnit = dictionaries.product_units[0];
  const firstStatus = dictionaries.product_statuses[0];

  return {
    product_category_id: '',
    product_brand_id: '',
    product_manufacturer_id: '',
    product_category_ids: [],
    product_properties: {},
    product_requires_unit_review: false,
    product_sku: '',
    product_name: '',
    product_unit_id: firstUnit === undefined ? '' : String(firstUnit.id),
    product_description: '',
    product_status_id: firstStatus === undefined ? '' : String(firstStatus.id),
  };
}

export function catalogCategoryToForm(category: AdminCatalogCategory): CatalogCategoryFormState {
  return {
    category_parent_id: formatOptionalFormNumber(category.category_parent_id),
    category_name: category.category_name,
    category_description: category.category_description ?? '',
    category_order: String(category.category_order),
  };
}

export function catalogProductToForm(product: AdminCatalogProduct): CatalogProductFormState {
  return {
    product_category_id: formatOptionalFormNumber(product.product_category_id),
    product_brand_id: formatOptionalFormNumber(product.product_brand_id),
    product_manufacturer_id: formatOptionalFormNumber(product.product_manufacturer_id),
    product_category_ids: (product.product_categories ?? []).map((category) => String(category.category_id)),
    product_properties: product.product_properties ?? {},
    product_requires_unit_review: product.product_requires_unit_review ?? false,
    product_sku: product.product_sku,
    product_name: product.product_name,
    product_unit_id: String(product.product_unit_id),
    product_description: product.product_description ?? '',
    product_status_id: String(product.product_status_id),
  };
}

export function createCatalogCategoryPayload(form: CatalogCategoryFormState): AdminCatalogCategoryCreateRequest | AdminCatalogCategoryUpdateRequest {
  return {
    category_parent_id: normalizeOptionalNumber(form.category_parent_id),
    category_name: form.category_name.trim(),
    category_description: normalizeOptionalStringInput(form.category_description),
    category_order: Number(form.category_order),
  };
}

export function createCatalogProductCreatePayload(form: CatalogProductFormState): AdminCatalogProductCreateRequest {
  return {
    product_category_id: normalizeOptionalNumber(form.product_category_id),
    product_brand_id: normalizeOptionalNumber(form.product_brand_id),
    product_manufacturer_id: normalizeOptionalNumber(form.product_manufacturer_id),
    product_category_ids: form.product_category_ids.map(Number),
    product_properties: normalizeCatalogProperties(form.product_properties),
    product_requires_unit_review: form.product_requires_unit_review,
    product_name: form.product_name.trim(),
    product_unit_id: Number(form.product_unit_id),
    product_description: normalizeOptionalStringInput(form.product_description),
    product_status_id: Number(form.product_status_id),
  };
}

export function createCatalogProductUpdatePayload(form: CatalogProductFormState): AdminCatalogProductUpdateRequest {
  const payload = createCatalogProductCreatePayload(form);

  return {
    ...payload,
    product_sku: form.product_sku.trim(),
  };
}

function formatOptionalFormNumber(value: number | null | undefined): string {
  return value == null ? '' : String(value);
}

function normalizeOptionalNumber(value: string): number | null {
  return value === '' ? null : Number(value);
}

function normalizeOptionalStringInput(value: string): string | null {
  const trimmedValue = value.trim();
  return trimmedValue === '' ? null : trimmedValue;
}

export const defaultCatalogCategoryForm: CatalogCategoryFormState = {
  category_parent_id: '', category_name: '', category_description: '', category_order: '100',
};
